import type { ResponseError } from "@lucaswickstrom/fetch";
import { useMutationWithAuthError } from "@ssf/auth-components";
import type { MutationOptions, QueryOptions } from "@ssf/components";
import { queryClient } from "@ssf/components";
import {
  getGame,
  getMember,
  getPairing,
  getPairingsFromTournaments,
  RoleType,
  updateGame,
} from "@ssf/memdb-api";
import type {
  QueryFunction,
  QueryKey,
  UseQueryOptions,
} from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";

export function useQueryWrapper<
  TQueryFnData = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  queryKey: TQueryKey,
  queryFn: QueryFunction<TQueryFnData, TQueryKey>,
  queryOptions?: Partial<
    UseQueryOptions<TQueryFnData, ResponseError | Error, TData, TQueryKey>
  >,
) {
  return useQuery({ queryKey, queryFn, ...queryOptions });
}

const getMyContendersWithSchema = (tournamentId: number) =>
  getMember("me", {
    roles: [
      {
        orgrole: 1,
        orgtype: 1,
        team: {
          id: 1,
          name: 1,
          participations: [
            {
              group: { id: 1, name: 1, class: { tournament: { id: 1 } } },
              filter: `(group.class.tournament.id=${tournamentId})`,
            },
          ],
        },
        end: 1,
        filter: "(&(&(orgrole=201)(end=1970-01-01))(orgtype=4))",
      },
    ],
  }).then(({ roles }) =>
    roles.flatMap(({ team: { participations, ...team } }) =>
      participations.map(({ group }) => ({
        groupId: group.id,
        groupName: group.name,
        ...team,
      })),
    ),
  );
export const useMyContenders = (
  tournamentId: number,
  options?: QueryOptions<typeof getMyContendersWithSchema>,
) =>
  useQueryWrapper(
    ["useMyContenders", tournamentId],
    () => getMyContendersWithSchema(tournamentId),
    options,
  );

const pairingContenderParams = {
  partid: 1,
  withdrawn: 1,
  name: 1,
  team: {
    club: {
      district: { id: 1 },
      clubStatus: [{ status: 1, year: 1 }],
    },
    roles: [
      {
        orgrole: 1,
        end: 1,
        member: { id: 1, name: 1 },
        filter: `(&(orgrole=${RoleType.TeamLeader1})(end=1970-01-01))`,
      },
    ],
    players: [{ member: { id: 1, name: 1 } }],
  },
};

const getPairingWithSchema = (pairingId: number) =>
  getPairing({
    pairingId,
    params: {
      schema: {
        id: 1,
        home: pairingContenderParams,
        away: pairingContenderParams,
        finalized: 1,
        publishednote: 1,
        awayresult: 1,
        homeresult: 1,
        games: [
          {
            board: 1,
            whiteid: 1,
            white: { id: 1, name: 1 },
            balckid: 1,
            black: { id: 1, name: 1 },
            result: 1,
          },
        ],
        group: {
          id: 1,
          name: 1,
          boards: 1,
          class: { tournament: { id: 1, name: 1 } },
        },
      },
    },
  });
export type Match = Awaited<ReturnType<typeof getPairingWithSchema>>;
export const useMatch = (
  matchId: number,
  options?: Partial<QueryOptions<typeof getPairingWithSchema>>,
) =>
  useQueryWrapper(
    ["useMatch", matchId],
    () => getPairingWithSchema(matchId),
    options,
  );

const getPairingsFromTournamentsWithSchema = ({
  tournamentIds,
  page,
  size,
  includeByes,
  sortBy,
  sortOrder,
  includeWithResults,
  includeWithoutResults,
}: {
  tournamentIds: number[];
  page: number;
  size: number;
  includeByes?: boolean;
  sortBy?: "publishDate" | "groupdId";
  sortOrder?: "asc" | "desc";
  includeWithResults?: boolean;
  includeWithoutResults?: boolean;
}) =>
  getPairingsFromTournaments({
    tournamentIds,
    page,
    size,
    includeByes,
    sortBy,
    sortOrder,
    includeWithResults,
    includeWithoutResults,
    schema: [
      {
        id: 1,
        home: {
          partid: 1,
          withdrawn: 1,
          team: { name: 1, school: { name: 1 } },
        },
        away: {
          partid: 1,
          withdrawn: 1,
          team: { name: 1, school: { name: 1 } },
        },
        finalized: 1,
        publishednote: 1,
        awayresult: 1,
        homeresult: 1,
        group: {
          name: 1,
          pairingsystem: 1,
          class: { tournament: { id: 1, name: 1 } },
        },
        publishdate: 1,
      },
    ],
  });
export type Matches = Awaited<
  ReturnType<typeof getPairingsFromTournamentsWithSchema>
>;
export const useMatches = ({
  tournamentIds,
  page,
  size,
  includeByes,
  sortBy,
  sortOrder,
  includeWithResults,
  includeWithoutResults,
}: {
  tournamentIds: number[];
  page: number;
  size: number;
  includeByes?: boolean;
  sortBy?: "publishDate" | "groupdId";
  sortOrder?: "asc" | "desc";
  includeWithResults?: boolean;
  includeWithoutResults?: boolean;
}) =>
  useQueryWrapper(
    [
      "useMatches",
      tournamentIds.join(","),
      page,
      size,
      includeByes,
      sortBy,
      sortOrder,
      includeWithResults,
      includeWithoutResults,
    ],
    () =>
      getPairingsFromTournamentsWithSchema({
        tournamentIds,
        page,
        size,
        includeByes,
        sortBy,
        sortOrder,
        includeWithResults,
        includeWithoutResults,
      }),
    { enabled: tournamentIds.length > 0 },
  );

const getGameWithSchema = ({
  matchId,
  board,
}: {
  matchId: number;
  board: number;
}) =>
  getGame(matchId, board, {
    schema: {
      pairingid: 1,
      board: 1,
    },
  });
export const useGame = (
  {
    matchId,
    board,
  }: {
    matchId: number;
    board: number;
  },
  options?: Partial<QueryOptions<typeof getGameWithSchema>>,
) =>
  useQueryWrapper(
    ["useGame", matchId, board],
    () => getGameWithSchema({ matchId, board }),
    options,
  );

export const useUpdateGame = (options?: MutationOptions<typeof updateGame>) =>
  useMutationWithAuthError(["useUpdateGame"], updateGame, {
    onSettled(_result, _error, game) {
      queryClient.invalidateQueries({ queryKey: ["useMatch", game.pairingid] });
    },
    ...options,
  });
